/**
 * In-app confirmation / prompt dialogs (replaces window.confirm / window.prompt).
 * Buttons: Cancel · Deny · Commit (Deny optional).
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';

export type ConfirmDialogProps = {
  open: boolean;
  title: string;
  message: string;
  /** Primary action label (default Commit) */
  commitLabel?: string;
  /** Secondary reject label; omit to hide Deny */
  denyLabel?: string;
  /** Dismiss label (default Cancel) */
  cancelLabel?: string;
  /** Destructive styling for Commit */
  danger?: boolean;
  loading?: boolean;
  onCommit: () => void | Promise<void>;
  onDeny?: () => void;
  onCancel: () => void;
};

export function ConfirmDialog({
  open,
  title,
  message,
  commitLabel = 'Commit',
  denyLabel = 'Deny',
  cancelLabel = 'Cancel',
  danger = false,
  loading = false,
  onCommit,
  onDeny,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;

  const body = (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40" onClick={loading ? undefined : onCancel} />
      <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="flex items-start gap-3 px-5 pt-5 pb-3">
          <div className={`mt-0.5 p-2 rounded-full shrink-0 ${danger ? 'bg-red-100 text-red-600' : 'bg-indigo-100 text-indigo-600'}`}>
            <AlertTriangle size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
            <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap">{message}</p>
          </div>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600 p-1 rounded-lg"
            onClick={onCancel}
            disabled={loading}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex flex-wrap justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-100">
          <button
            type="button"
            disabled={loading}
            onClick={onCancel}
            className="px-3.5 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          {onDeny && (
            <button
              type="button"
              disabled={loading}
              onClick={onDeny}
              className="px-3.5 py-2 text-sm font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 disabled:opacity-50"
            >
              {denyLabel}
            </button>
          )}
          <button
            type="button"
            disabled={loading}
            onClick={() => void onCommit()}
            className={`px-3.5 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${
              danger ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {loading ? 'Working…' : commitLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}

export type PromptDialogProps = {
  open: boolean;
  title: string;
  message?: string;
  label?: string;
  initialValue?: string;
  placeholder?: string;
  commitLabel?: string;
  denyLabel?: string;
  cancelLabel?: string;
  onCommit: (value: string) => void | Promise<void>;
  onDeny?: () => void;
  onCancel: () => void;
};

export function PromptDialog({
  open,
  title,
  message,
  label = 'Name',
  initialValue = '',
  placeholder = '',
  commitLabel = 'Commit',
  denyLabel = 'Deny',
  cancelLabel = 'Cancel',
  onCommit,
  onDeny,
  onCancel,
}: PromptDialogProps) {
  const [value, setValue] = useState(initialValue);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  if (!open) return null;

  const submit = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      await onCommit(trimmed);
    } finally {
      setLoading(false);
    }
  };

  const body = (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40" onClick={loading ? undefined : onCancel} />
      <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
            <button
              type="button"
              className="text-slate-400 hover:text-slate-600 p-1 rounded-lg"
              onClick={onCancel}
              disabled={loading}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
          {message && <p className="mt-1 text-sm text-slate-600">{message}</p>}
          <label className="block mt-4 text-xs font-medium text-slate-600 uppercase tracking-wide">{label}</label>
          <input
            autoFocus
            className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={value}
            placeholder={placeholder}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
              if (e.key === 'Escape') onCancel();
            }}
            disabled={loading}
          />
        </div>
        <div className="flex flex-wrap justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-100">
          <button
            type="button"
            disabled={loading}
            onClick={onCancel}
            className="px-3.5 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          {onDeny && (
            <button
              type="button"
              disabled={loading}
              onClick={onDeny}
              className="px-3.5 py-2 text-sm font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 disabled:opacity-50"
            >
              {denyLabel}
            </button>
          )}
          <button
            type="button"
            disabled={loading || !value.trim()}
            onClick={() => void submit()}
            className="px-3.5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Working…' : commitLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}
